import {
  Mutation,
  MutationSubmitPushToCardTransferArgs,
  PushToCardTransferStatus,
  Query,
  SubmitPushToCardTransferError,
  SubmitPushToCardTransferErrorCode,
  SubmitPushToCardTransferResultTypenames,
  SubmitTransferInput,
  SubmitTransferResultTypenames,
  TransferRail,
  TransferReviewDetails,
} from '@varo/graphql-schema-types';
import { useNavigation, useRouter } from 'expo-router';
import type { NavigationAction } from 'expo-router/react-navigation';
import {
  ANALYTICS_SOURCES,
  apiErrorFailure,
  requestErrorFailure,
  type ReviewedTransfer,
  reviewedTransferAttributes,
  SUBMIT_OUTCOME_UNKNOWN,
  type TransferFailure,
} from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import useTransferAnalytics from 'features/feeBasedInstantTransfer/hooks/useTransferAnalytics';
import {
  submitErrorAtom,
  type SubmitServerError,
  submitSuccessAtom,
  type SubmitSuccessContent,
} from 'features/feeBasedInstantTransfer/state/submitOutcomeAtoms';
import { useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import SubmitPushToCardTransfer from 'services/graphql/mutations/MoveMoney/SubmitPushToCardTransfer';
import SubmitTransfer from 'services/graphql/mutations/MoveMoney/SubmitTransfer';
import { FetchHomeTilesForIds } from 'services/graphql/queries/FetchHomeTilesV2';
import { TRANSFER_MONEY_SUCCESSFUL } from 'shared/analytics/events/transferMoney';
import homeTileTypes from 'shared/constants/homeTileTypes';
import { isWeb } from 'shared/constants/platform';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import usePreventBackNavigation from 'shared/hooks/usePreventBackNavigation/usePreventBackNavigation';
import { classifyError } from 'shared/utils/errors/classifyError';
import { getUuid } from 'shared/utils/getUuid/getUuid';

type ReviewedQuote = Pick<
  TransferReviewDetails,
  'rail' | 'transferQuoteToken' | 'transferDetails'
>;

type SubmitOutcome =
  | ({ kind: 'settled' } & SubmitSuccessContent)
  | { kind: 'processing'; transferId?: string }
  | {
      kind: 'rejected';
      failure: TransferFailure;
      serverError?: SubmitServerError;
    }
  | { kind: 'unknown'; failure: TransferFailure }
  | { kind: 'aborted' };

type SubmitStatus = 'idle' | 'submitting' | 'outcomeUnknown';

const MISSING_RESULT_OUTCOME: SubmitOutcome = {
  kind: 'unknown',
  failure: { reason: SUBMIT_OUTCOME_UNKNOWN, type: 'unknown' },
};

const pushToCardErrorOutcome = ({
  code,
  errorCode,
  title,
  message,
  actions,
}: Pick<
  SubmitPushToCardTransferError,
  'code' | 'errorCode' | 'title' | 'message' | 'actions'
>): SubmitOutcome => {
  const failure = apiErrorFailure(code || errorCode, message);
  return errorCode === SubmitPushToCardTransferErrorCode.Unavailable
    ? {
        kind: 'unknown',
        failure: { ...failure, reason: SUBMIT_OUTCOME_UNKNOWN },
      }
    : {
        kind: 'rejected',
        failure,
        serverError: { title, message, actions },
      };
};

const requestErrorOutcome = (error: unknown): SubmitOutcome => {
  const { type } = classifyError(error);
  switch (type) {
    case 'abort':
      return { kind: 'aborted' };
    case 'auth':
    case 'client':
      return {
        kind: 'rejected',
        failure: requestErrorFailure(error),
      };
    case 'network':
    case 'timeout':
    case 'server':
    case 'graphql':
    case 'unknown':
      return {
        kind: 'unknown',
        failure: requestErrorFailure(error, SUBMIT_OUTCOME_UNKNOWN),
      };
  }
};

type SubmitVariables = SubmitTransferInput & { idempotencyId: string };

export default function useSubmitReviewedTransfer(
  transfer: ReviewedTransfer | null,
  quote: ReviewedQuote | undefined,
) {
  const router = useRouter();
  const navigation = useNavigation();
  const [idempotencyId] = useState(getUuid);
  const setSubmitError = useSetAtom(submitErrorAtom);
  const setSubmitSuccess = useSetAtom(submitSuccessAtom);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const isSubmittingRef = useRef(false);
  const [, submitTransfer] = useCancellableMutation<
    Pick<Mutation, 'submitTransfer'>,
    SubmitVariables
  >(SubmitTransfer);
  const [, submitPushToCardTransfer] = useCancellableMutation<
    Pick<Mutation, 'submitPushToCardTransfer'>,
    MutationSubmitPushToCardTransferArgs
  >(SubmitPushToCardTransfer);
  const [refreshHomeTiles] =
    useCancellableQuery<Pick<Query, 'tiles'>>(FetchHomeTilesForIds);
  const [blockedBackAction, setBlockedBackAction] =
    useState<NavigationAction | null>(null);
  const { logTransferEvent, logFlowError, logRequestCompleted } =
    useTransferAnalytics(ANALYTICS_SOURCES.confirm);

  const isOutcomePending = status !== 'idle';

  // An unknown outcome may still post, so leaving to re-review would resubmit under a new idempotency id.
  usePreventBackNavigation(isOutcomePending, (action) => {
    if (status === 'submitting') {
      setBlockedBackAction(action);
    }
  });

  useEffect(() => {
    if (!isWeb || !isOutcomePending) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOutcomePending]);

  const refreshTilesThen = async (
    tileIds: string[],
    outcome: SubmitOutcome,
  ): Promise<SubmitOutcome> => {
    if (tileIds.length === 0) {
      return outcome;
    }
    try {
      await refreshHomeTiles({ ids: tileIds, tileTypes: homeTileTypes });
      logRequestCompleted('fetch_home_tiles', {
        hasResolvedWithoutResult: true,
      });
    } catch (error) {
      if (error instanceof AbortError) {
        return { kind: 'aborted' };
      }
      logRequestCompleted('fetch_home_tiles', { error });
    }
    return outcome;
  };

  const submitAccountTransfer = async (
    reviewed: ReviewedTransfer,
    rail: TransferRail,
  ): Promise<SubmitOutcome> => {
    let response: Awaited<ReturnType<typeof submitTransfer>>;
    try {
      response = await submitTransfer({
        sourceAccountId: reviewed.source.id,
        destinationAccountId: reviewed.destination.id,
        amount: reviewed.amount,
        currency: reviewed.currency,
        idempotencyId,
      });
    } catch (error) {
      const outcome = requestErrorOutcome(error);
      if (outcome.kind !== 'aborted') {
        logRequestCompleted('submit_transfer', { error });
      }
      return outcome;
    }
    const result = response.data?.submitTransfer;
    logRequestCompleted('submit_transfer', {
      result,
      error: response.error,
      extensions: response.extensions,
    });
    if (response.error) {
      return requestErrorOutcome(response.error);
    }
    if (
      result?.__typename === SubmitTransferResultTypenames.SubmitTransferError
    ) {
      return {
        kind: 'rejected',
        failure: apiErrorFailure(result.code, result.message),
      };
    }
    if (
      result?.__typename !== SubmitTransferResultTypenames.SubmitTransferSuccess
    ) {
      return MISSING_RESULT_OUTCOME;
    }
    logTransferEvent(TRANSFER_MONEY_SUCCESSFUL, {
      ...reviewedTransferAttributes(reviewed, rail, quote?.transferDetails),
      transfer_id: result.id,
    });
    return refreshTilesThen(result.tileIdsToRefresh, {
      kind: 'settled',
      rail,
      amount: reviewed.amount,
      transferSpeedMessage: result.transferSpeedMessage ?? undefined,
    });
  };

  const submitPushToCard = async (
    reviewed: ReviewedTransfer,
    rail: TransferRail,
  ): Promise<SubmitOutcome> => {
    const transferQuoteToken = quote?.transferQuoteToken;
    if (!transferQuoteToken) {
      return {
        kind: 'rejected',
        failure: { reason: 'transfer_quote_token_missing', type: 'client' },
      };
    }
    let response: Awaited<ReturnType<typeof submitPushToCardTransfer>>;
    try {
      response = await submitPushToCardTransfer({
        idempotencyId,
        input: {
          source: reviewed.source,
          destination: reviewed.destination,
          principalAmount: {
            amount: reviewed.amount,
            currency: reviewed.currency,
          },
          transferQuoteToken,
        },
      });
    } catch (error) {
      const outcome = requestErrorOutcome(error);
      if (outcome.kind !== 'aborted') {
        logRequestCompleted('submit_push_to_card_transfer', { error });
      }
      return outcome;
    }
    const result = response.data?.submitPushToCardTransfer;
    logRequestCompleted('submit_push_to_card_transfer', {
      result,
      error: response.error,
      extensions: response.extensions,
    });
    if (response.error) {
      return requestErrorOutcome(response.error);
    }
    if (
      result?.__typename ===
      SubmitPushToCardTransferResultTypenames.SubmitPushToCardTransferError
    ) {
      return pushToCardErrorOutcome(result);
    }
    if (
      result?.__typename !==
      SubmitPushToCardTransferResultTypenames.SubmitPushToCardTransferSuccess
    ) {
      return MISSING_RESULT_OUTCOME;
    }
    const transferId = result.principalTransferId ?? undefined;
    // UNDEFINED is an accepted but unclassified transfer, so only SETTLED can claim the money arrived.
    if (result.status !== PushToCardTransferStatus.Settled) {
      return refreshTilesThen(result.tileIdsToRefresh, {
        kind: 'processing',
        transferId,
      });
    }
    logTransferEvent(TRANSFER_MONEY_SUCCESSFUL, {
      ...reviewedTransferAttributes(reviewed, rail, quote?.transferDetails),
      transfer_id: transferId,
    });
    return refreshTilesThen(result.tileIdsToRefresh, {
      kind: 'settled',
      rail,
      amount: reviewed.amount,
    });
  };

  const submitters: Partial<
    Record<
      TransferRail,
      (reviewed: ReviewedTransfer, rail: TransferRail) => Promise<SubmitOutcome>
    >
  > = {
    [TransferRail.Ach]: submitAccountTransfer,
    [TransferRail.Internal]: submitAccountTransfer,
    [TransferRail.PushToCard]: submitPushToCard,
  };

  const finishSubmission = (
    outcome: SubmitOutcome,
    reviewed: ReviewedTransfer,
    rail: TransferRail,
  ) => {
    switch (outcome.kind) {
      case 'settled': {
        const outcomeId = getUuid();
        setSubmitSuccess({
          outcomeId,
          rail: outcome.rail,
          amount: outcome.amount,
          transferSpeedMessage: outcome.transferSpeedMessage,
        });
        router.dismissTo('/move-money/transfer/instant-transfer');
        router.dismissTo({
          pathname: '/move-money/transfer/instant-transfer-success',
          params: { outcomeId },
        });
        return;
      }
      case 'processing':
        router.dismissTo('/move-money/transfer/instant-transfer');
        router.dismissTo({
          pathname: '/move-money/transfer/transfer-processing',
          params: { transferId: outcome.transferId },
        });
        return;
      case 'rejected': {
        const outcomeId = getUuid();
        setSubmitError({
          outcomeId,
          rail,
          errorReason: outcome.failure.reason,
          reviewed,
          serverError: outcome.serverError,
        });
        logFlowError(
          'submit',
          outcome.failure,
          reviewedTransferAttributes(reviewed, rail, quote?.transferDetails),
        );
        router.dismissTo('/move-money/transfer/instant-transfer');
        router.dismissTo({
          pathname: '/move-money/transfer/instant-transfer-error',
          params: { outcomeId },
        });
        return;
      }
      case 'unknown':
        logFlowError(
          'submit',
          outcome.failure,
          reviewedTransferAttributes(reviewed, rail, quote?.transferDetails),
        );
        isSubmittingRef.current = false;
        setBlockedBackAction(null);
        setStatus('outcomeUnknown');
        return;
      case 'aborted':
        return;
    }
  };

  const submit = async () => {
    const rail = quote?.rail;
    if (!transfer || !rail || isSubmittingRef.current) {
      return;
    }
    const submitter = submitters[rail];
    if (!submitter) {
      logFlowError(
        'submit',
        { reason: 'unsupported_transfer_rail', type: 'client', message: rail },
        reviewedTransferAttributes(transfer, rail, quote.transferDetails),
      );
      return;
    }
    isSubmittingRef.current = true;
    setStatus('submitting');
    finishSubmission(await submitter(transfer, rail), transfer, rail);
  };

  const confirmLeave = () => {
    if (blockedBackAction) {
      navigation.dispatch(blockedBackAction);
    }
  };

  const cancelLeave = () => {
    setBlockedBackAction(null);
  };

  return {
    status,
    submit,
    isConfirmingLeave: blockedBackAction !== null,
    confirmLeave,
    cancelLeave,
  };
}
